import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { X, KeyRound } from "lucide-react";

export default function ChangePasswordModal({ open, onClose, forced = false, onDone }) {
  const { t } = useI18n();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (newPw.length < 6) return toast.error(t("toast.password_min"));
    if (newPw !== confirm) return toast.error(t("toast.passwords_mismatch"));
    setLoading(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success(t("toast.password_changed"));
      setOldPw(""); setNewPw(""); setConfirm("");
      onDone && onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="change-pw-modal">
      <div className="w-full max-w-md card-flat p-8" style={{background:"var(--surface)"}}>
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="label-caps flex items-center gap-2"><KeyRound size={12} /> {t("security.title")}</div>
            <div className="font-display text-2xl mt-1">{forced ? t("security.forced_title") : t("nav.change_password")}</div>
          </div>
          {!forced && <button onClick={onClose}><X size={18} /></button>}
        </div>
        {forced && (
          <div className="border border-[--gold] p-3 mb-6 text-xs text-neutral-300" style={{borderColor:"var(--gold)"}}>
            {t("security.forced_desc")}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="label-caps block mb-2">{t("security.current_pw")}</label>
            <input type="password" value={oldPw} onChange={(e)=>setOldPw(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="cpw-old" />
          </div>
          <div>
            <label className="label-caps block mb-2">{t("security.new_pw")}</label>
            <input type="password" value={newPw} onChange={(e)=>setNewPw(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="cpw-new" />
          </div>
          <div>
            <label className="label-caps block mb-2">{t("security.new_pw_confirm")}</label>
            <input type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="cpw-confirm" />
          </div>
        </div>
        <button onClick={submit} disabled={loading} className="btn-primary-gold w-full mt-6" data-testid="cpw-submit">
          {loading ? t("security.changing") : t("security.change_button")}
        </button>
      </div>
    </div>
  );
}
